import { useEffect } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { Layout } from './components/Layout.js';
import { Login, NovaSenha } from './components/Login.js';
import { FonteProvider } from './dados/contexto.js';
import { criarFonteDemo } from './dados/demo.js';
import { criarFonteLocal } from './dados/fonte-local.js';
import { env } from './lib/env.js';
import { startOpportunisticSync } from './lib/local.js';
import { lerModo } from './lib/modo.js';
import { useAuth } from './lib/useAuth.js';
import { usuarioLocal } from './lib/usuario.js';
import { Animais } from './paginas/Animais.js';
import { Coleta } from './paginas/Coleta.js';
import { Comparar } from './paginas/Comparar.js';
import { Dados } from './paginas/Dados.js';
import { EditarVisita } from './paginas/EditarVisita.js';
import { Equipe } from './paginas/Equipe.js';
import { FichaAnimal } from './paginas/FichaAnimal.js';
import { FormAnimal } from './paginas/FormAnimal.js';
import { FormHaras } from './paginas/FormHaras.js';
import { Fotogrametria } from './paginas/Fotogrametria.js';
import { ImportarAnimais } from './paginas/ImportarAnimais.js';
import { Inicio } from './paginas/Inicio.js';
import { ListaHaras } from './paginas/Haras.js';
import { Laudo } from './paginas/Laudo.js';
import { PaginaMidia } from './paginas/Midia.js';
import { Repetibilidade } from './paginas/Repetibilidade.js';
import { NovaVisita } from './paginas/NovaVisita.js';
import { Sincronizacao } from './paginas/Sincronizacao.js';
import { PaginaVisita } from './paginas/Visita.js';
import { Visitas } from './paginas/Visitas.js';

/*
  O interruptor "Dados ficticios" (FaixaDeContexto) escolhe a fonte: ficticia (so leitura) ou o banco
  deste aparelho (com cadastro). Trocar recarrega o app — as duas nunca se
  misturam na mesma tela.
*/
const fonte = lerModo() === 'demo' ? criarFonteDemo() : criarFonteLocal(usuarioLocal);

/**
 * Tela de tarefa (formulario, coleta, fotogrametria): no celular esconde a
 * navegacao de baixo e deixa o pe da tela para as acoes da propria tarefa.
 */
const TAREFA = { tarefa: true } as const;

const router = createBrowserRouter([
  // O laudo e uma folha para imprimir: fora do Layout, sem menu nem rodape.
  { path: 'animais/:id/laudo', element: <Laudo /> },
  {
    element: <Layout />,
    children: [
      { index: true, element: <Inicio /> },
      { path: 'animais', element: <Animais /> },
      { path: 'animais/novo', element: <FormAnimal />, handle: TAREFA },
      { path: 'animais/importar', element: <ImportarAnimais />, handle: TAREFA },
      { path: 'animais/:id', element: <FichaAnimal /> },
      { path: 'animais/:id/editar', element: <FormAnimal />, handle: TAREFA },
      { path: 'animais/:id/midia/:midiaId', element: <PaginaMidia /> },
      { path: 'animais/:id/midia/:midiaId/angulos', element: <Fotogrametria />, handle: TAREFA },
      { path: 'comparar', element: <Comparar /> },
      { path: 'sincronizacao', element: <Sincronizacao /> },
      { path: 'dados', element: <Dados /> },
      { path: 'equipe', element: <Equipe /> },
      { path: 'repetibilidade', element: <Repetibilidade /> },
      { path: 'haras', element: <ListaHaras /> },
      { path: 'haras/novo', element: <FormHaras />, handle: TAREFA },
      { path: 'haras/:id/editar', element: <FormHaras />, handle: TAREFA },
      { path: 'visitas', element: <Visitas /> },
      { path: 'visitas/nova', element: <NovaVisita />, handle: TAREFA },
      { path: 'visitas/:id', element: <PaginaVisita /> },
      { path: 'visitas/:id/editar', element: <EditarVisita />, handle: TAREFA },
      { path: 'visitas/:id/fichas/:fichaId', element: <Coleta />, handle: TAREFA },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

export function App() {
  const { session, loading, recuperandoSenha, terminarRecuperacao } = useAuth();

  useEffect(() => {
    if (!session) return;
    return startOpportunisticSync();
  }, [session]);

  if (loading) {
    return <div className="flex min-h-dvh items-center justify-center text-apagado">Abrindo…</div>;
  }

  // Sem sessao, so abre com o login desligado: em dev (VITE_DEV_SEM_LOGIN) ou na build de previa.
  if (!session && !env.semLogin) return <Login />;
  if (session && recuperandoSenha) return <NovaSenha aoTerminar={terminarRecuperacao} />;

  return (
    <FonteProvider fonte={fonte}>
      <RouterProvider router={router} />
    </FonteProvider>
  );
}
